import React, { useState, Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Folder, ChevronRight, ChevronDown, Check, FolderPlus } from "lucide-react";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import EventSystem from "../../events/EventSystem";
import TreeView, { ITreeViewOnExpandProps } from "../TreeView";
import { flattenTree } from "../TreeView/utils";
import {
  bmpIcon,
  csvIcon,
  dgnIcon,
  dwfIcon,
  dwgIcon,
  dxfIcon,
  gifIcon,
  jpgIcon,
  jsonIcon,
  pcfIcon,
  pdfIcon,
  pngIcon,
  svgIcon,
  tiffIcon,
  tifIcon,
  unkownIcon,
} from "../FolderListRightPanel/icons";

const FolderIcon = ({ isOpen, own }: { isOpen: boolean; own: boolean }) =>
  isOpen ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={own ? "#FCDC12" : "#e8a87c"}
      className="size-6 mr-1 flex-shrink-0"
    >
      <path d="M19.906 9c.382 0 .749.057 1.094.162V9a3 3 0 0 0-3-3h-3.879a.75.75 0 0 1-.53-.22L11.47 3.66A2.25 2.25 0 0 0 9.879 3H6a3 3 0 0 0-3 3v3.162A3.756 3.756 0 0 1 4.094 9h15.812ZM4.094 10.5a2.25 2.25 0 0 0-2.227 2.568l.857 6A2.25 2.25 0 0 0 4.951 21H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-2.227-2.568H4.094Z" />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={own ? "#FCDC12" : "#e8a87c"}
      className="size-6 mr-1 flex-shrink-0"
    >
      <path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z" />
    </svg>
  );

const FileIconSvg = ({ filename }: { filename: string }) => {
  if (filename.toLocaleLowerCase().endsWith(".json")) return jsonIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dwg")) return dwgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dxf")) return dxfIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dwf")) return dwfIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dgn")) return dgnIcon;
  else if (filename.toLocaleLowerCase().endsWith(".tiff")) return tiffIcon;
  else if (filename.toLocaleLowerCase().endsWith(".tif")) return tifIcon;
  else if (filename.toLocaleLowerCase().endsWith(".pdf")) return pdfIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".svg") ||
    filename.toLocaleLowerCase().endsWith(".svgz")
  )
    return svgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".gif")) return gifIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".jpg") ||
    filename.toLocaleLowerCase().endsWith(".jpeg")
  )
    return jpgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".png")) return pngIcon;
  else if (filename.toLocaleLowerCase().endsWith(".bmp")) return bmpIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".xls") ||
    filename.toLocaleLowerCase().endsWith(".xlsx") ||
    filename.toLocaleLowerCase().endsWith(".csv")
  )
    return csvIcon;
  else if (filename.toLocaleLowerCase().endsWith(".pcf")) return pcfIcon;
  else return unkownIcon;
};

interface FolderSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (path: string) => void;
  folderStructure: any[];
  selectType?: "folder" | "file";
  title?: string;
}

export const FolderSelectModal: React.FC<FolderSelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  folderStructure,
  selectType = "folder",
  title,
}) => {
  const { config } = useConfig();
  const { ServerBackEndUrl } = config;
  const { user } = React.useContext(CadviewerContext);

  const [selectedPath, setSelectedPath] = useState<string>("");
  const [expandedIds, setExpandedIds] = useState<any[]>([]);
  const [error, setError] = useState("");

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const getCleanPath = (rawStr: string) => {
    let path = rawStr ? rawStr.replace(/\\/g, "/") : "";
    const contentIndex = path.indexOf("/content/");
    if (contentIndex !== -1) {
      path = path.substring(contentIndex + "/content/".length);
    } else if (path.startsWith("content/")) {
      path = path.substring("content/".length);
    } else if (path.startsWith("../content/")) {
      path = path.substring("../content/".length);
    }
    
    if (path.startsWith("versions/")) {
      path = path.substring("versions/".length);
    }
    return path;
  };

  const rootPath = React.useMemo(() => {
    if (folderStructure && folderStructure.length > 0) {
      const firstItemPath = folderStructure[0].path;
      if (firstItemPath) {
        const cleanItemPath = getCleanPath(firstItemPath);
        const parts = cleanItemPath.split('/');
        parts.pop(); // Remove the item's own name
        return parts.join('/');
      }
    }
    return "";
  }, [folderStructure]);

  const absoluteRootPath = React.useMemo(() => {
    if (folderStructure && folderStructure.length > 0) {
      const firstItemPath = folderStructure[0].path || "";
      const isWindows = firstItemPath.includes('\\');
      const separator = isWindows ? '\\' : '/';
      const parts = firstItemPath.split(separator);
      parts.pop();
      return parts.join(separator);
    }
    return "";
  }, [folderStructure]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setIsCreating(true);
    try {
      const parentAbsolute = selectedPath || absoluteRootPath;
      const separator = parentAbsolute.includes('\\') ? '\\' : '/';
      const newAbsoluteSelectedPath = parentAbsolute ? `${parentAbsolute}${separator}${newFolderName.trim()}` : newFolderName.trim();
      
      // Relative to the tree's own root, which IS the content root the server
      // will confine the write to. getCleanPath only strips "/content/", so it
      // still carries the storage key — sending that would ask the server to
      // create <content root>/<key>/… inside a path that already ends in it.
      const relative = selectedPath.startsWith(absoluteRootPath)
        ? selectedPath.slice(absoluteRootPath.length).split(separator).filter(Boolean).join('/')
        : '';
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      if (user?.token) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }
      // upload/folder, not settings/folders/create: the latter creates a
      // directory under a raw request path with nothing confining it, and is
      // switched off on a multi-tenant server — which is why this button
      // answered "Failed to create folder" on every subdomain. This one is
      // confined to the caller's own content root.
      const response = await fetch(
        ServerBackEndUrl + (ServerBackEndUrl.endsWith("/") ? "" : "/") + "upload/folder",
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            // "." is the tree's root — the route refuses an empty path.
            path: relative || '.',
            folder_name: newFolderName.trim(),
            global: true
          })
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // The server's own words: "route disabled in cloud mode" or "path not
        // allowed" tell the operator what to do; the generic message did not.
        throw new Error(data.error || data.message || `Failed to create folder (${response.status})`);
      }
      
      setSelectedPath(newAbsoluteSelectedPath);
      setNewFolderName("");
      setIsCreatingFolder(false);
      EventSystem.publish("panel.folder_list.refresh", {});
    } catch (err: any) {
      setError(err.message || "Error creating folder");
    } finally {
      setIsCreating(false);
    }
  };

  const folder = React.useMemo(
    () => ({
      name: "",
      children: (folderStructure || []).map((f) => ({
        own: f.own,
        name: f.name,
        path: f.path,
        modified: f.modified,
        isFolder: f.isFolder,
        children: f.children,
      })),
    }),
    [folderStructure]
  );

  const data = React.useMemo(() => flattenTree(folder), [folder]);

  // Reset state on modal open
  useEffect(() => {
    if (isOpen) {
      setError("");
      setIsCreatingFolder(false);
      setNewFolderName("");
      setSelectedPath("");
    }
  }, [isOpen]);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                      <Folder className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        {title ? title : (selectType === "folder" ? "Select Destination Folder" : "Select File")}
                      </Dialog.Title>
                      <div className="mt-4 space-y-4">
                        
                        {/* Folder/File Browser */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                              {selectType === "folder" ? "Choose a folder below" : "Choose a file below"}
                            </label>
                            {selectType === "folder" && (
                              <button 
                                onClick={() => setIsCreatingFolder(!isCreatingFolder)}
                                className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm font-medium"
                                title="Create New Folder"
                              >
                                <FolderPlus size={16} className="mr-1" /> New Folder
                              </button>
                            )}
                          </div>
                          
                          {selectType === "folder" && isCreatingFolder && (
                            <div className="mb-2 flex items-center gap-2 bg-indigo-50 p-2 rounded-md border border-indigo-100">
                              <input 
                                type="text" 
                                className="block w-full rounded-md border-0 py-1 px-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600" 
                                placeholder="Folder name..."
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                autoFocus
                              />
                              <button 
                                onClick={handleCreateFolder}
                                disabled={!newFolderName.trim() || isCreating}
                                className="bg-indigo-600 text-white px-2 py-1 rounded text-sm disabled:opacity-50"
                              >
                                Create
                              </button>
                              <button 
                                onClick={() => { setIsCreatingFolder(false); setNewFolderName(""); }}
                                className="text-gray-500 hover:text-gray-700 px-2 py-1 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          )}

                          <div className="border border-gray-300 rounded-md h-64 overflow-y-auto p-2 bg-gray-50 directory">
                            {data && data.length > 0 ? (
                              <TreeView
                                data={data}
                                aria-label="directory tree"
                                expandedIds={expandedIds}
                                onExpand={(node: ITreeViewOnExpandProps) => {
                                  if (node.isExpanded) {
                                    setExpandedIds([...expandedIds, node.element.id]);
                                  } else {
                                    setExpandedIds(
                                      expandedIds.filter((id) => id !== node.element.id)
                                    );
                                  }
                                }}
                                nodeRenderer={({
                                  element,
                                  isBranch,
                                  isExpanded,
                                  getNodeProps,
                                  level,
                                }) => {
                                  const nodePath = element.metadata?.path as string;
                                  const isSelected = isBranch && selectedPath === nodePath;
                                  
                                  const props = getNodeProps();
                                  const originalOnClick = props.onClick;

                                  return (
                                    <div
                                      {...props}
                                      onClick={(e) => {
                                        if (isBranch && nodePath !== undefined && selectType === "folder") {
                                          if (selectedPath === nodePath) {
                                            setSelectedPath("");
                                          } else {
                                            setSelectedPath(nodePath);
                                          }
                                        } else if (!isBranch && nodePath !== undefined && selectType === "file") {
                                          if (selectedPath === nodePath) {
                                            setSelectedPath("");
                                          } else {
                                            setSelectedPath(nodePath);
                                          }
                                        }
                                        if (originalOnClick) originalOnClick(e);
                                      }}
                                      className={`flex items-center text-sm cursor-pointer my-1 w-full rounded-md px-2 py-1 ${
                                        isSelected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700 hover:bg-slate-100"
                                      }`}
                                      style={{ paddingLeft: 16 * (level - 1) + 8 }}
                                    >
                                      {isBranch ? (
                                        <FolderIcon isOpen={isExpanded} own={element.own === true} />
                                      ) : (
                                        <FileIconSvg filename={element.name || ""} />
                                      )}
                                      <span className="w-full truncate">{element.name}</span>
                                      {isSelected && <Check size={14} className="ml-auto flex-shrink-0 text-indigo-600" />}
                                    </div>
                                  );
                                }}
                              />
                            ) : (
                              <p className="text-sm text-gray-500 text-center mt-16">No folders available</p>
                            )}
                          </div>
                        </div>

                        {error && (
                          <p className="text-sm text-red-600 mt-2">{error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                        const clean = selectedPath ? getCleanPath(selectedPath) : "";
                        onSelect(clean);
                        onClose();
                    }}
                    disabled={!selectedPath}
                  >
                    Select
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FolderSelectModal;
